/**
 * Core data offline: on every online start (and when the network comes back) the persisted core
 * queries are refreshed with the same keys and fetchers the screens use, so the calendar, directory
 * and library open offline later. All of them carry meta.persist (TanStack persister → IndexedDB):
 *   ['me'] (AuthProvider) · ['members'] · ['songs'] · ['collections'] · ['events', from, to] (today−7 … today+60)
 */
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { roleAtLeast } from '../../shared/audience';
import type { MeDTO } from '../../shared/dto/auth';
import type { MemberDTO } from '../../shared/dto/members';
import { addDays, toDateStr, weekStart } from '../../shared/time';
import { prefetchEvents } from '../api/calendar';
import { api, serverNow } from '../api/client';
import { COLLECTIONS_KEY, fetchCollections, fetchSongs, SONGS_KEY } from '../api/library';
import { fetchMember, MEMBERS_KEY, memberKey } from '../api/members';
import { isQueryGone } from '../api/queryClient';

export const CORE_EVENTS_BACK_DAYS = 7;
export const CORE_EVENTS_AHEAD_DAYS = 60;

/**
 * The calendar range kept offline: at least today − 7 … today + 60 (inclusive dates), anchored to
 * the Monday of this week so the persisted query key changes weekly, not daily.
 */
export function coreEventsRange(now: number): { from: string; to: string } {
  const monday = weekStart(toDateStr(now));
  return { from: addDays(monday, -CORE_EVENTS_BACK_DAYS), to: addDays(monday, CORE_EVENTS_AHEAD_DAYS + 6) };
}

let running: Promise<void> | null = null;
let lastRunAt = 0;

/** Refreshes the core queries (no-op while offline). Never rejects. */
export function prefetchCore(qc: QueryClient): Promise<void> {
  if (running) return running;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return Promise.resolve();
  running = (async () => {
    const meState = qc.getQueryState(['me']);
    const { from, to } = coreEventsRange(serverNow());
    await Promise.allSettled([
      // ['me'] is owned (and its fetcher defined) by AuthProvider: refetch the existing query.
      !meState || Date.now() - meState.dataUpdatedAt > 60_000 ? qc.refetchQueries({ queryKey: ['me'], exact: true }) : Promise.resolve(),
      qc.prefetchQuery({ queryKey: MEMBERS_KEY, queryFn: ({ signal }) => api<MemberDTO[]>('/members', { signal }), meta: { persist: true }, staleTime: 60_000 }),
      qc.prefetchQuery({ queryKey: SONGS_KEY, queryFn: ({ signal }) => fetchSongs(signal), meta: { persist: true }, staleTime: 60_000 }),
      qc.prefetchQuery({ queryKey: COLLECTIONS_KEY, queryFn: ({ signal }) => fetchCollections(signal), meta: { persist: true }, staleTime: 60_000 }),
    ]);
    prefetchEvents(qc, from, to);
    await pruneMemberDetails(qc);
    lastRunAt = Date.now();
  })().finally(() => {
    running = null;
  });
  return running;
}

/**
 * Single-member queries (['member', id], persisted) of people the server no longer shows —
 * deactivated or erased — are dropped, whether or not their page is opened again. The directory
 * (GET /members: active and on-leave members) only says whom to ask: invited members are visible
 * (GET /members/:id) but never listed, so an entry it does not list is fetched again and dropped only
 * when the server answers 403/404 (or already did). No answer (offline, 5xx) keeps it.
 * An open profile (an observer) is never touched: removing a query under its observer leaves the
 * screen on a query nothing updates any more (an endless spinner); MemberScreen handles 403/404.
 * Admins are skipped: they may open inactive members, which the directory does not list.
 */
export async function pruneMemberDetails(qc: QueryClient): Promise<void> {
  const me = qc.getQueryData<MeDTO | null>(['me']);
  if (!me || roleAtLeast(me.role, 'admin')) return;
  const list = qc.getQueryState<MemberDTO[]>(MEMBERS_KEY);
  if (!list?.data || list.status !== 'success') return;
  const listed = new Set(list.data.map((m) => m.id));
  const unlisted = qc
    .getQueryCache()
    .findAll({ queryKey: ['member'] })
    .flatMap((q) => {
      const id = q.queryKey[1];
      return typeof id === 'string' && !listed.has(id) && q.state.data !== undefined ? [{ id, q }] : [];
    });
  await Promise.allSettled(
    unlisted.map(async ({ id, q }) => {
      if (q.getObserversCount() > 0) return;
      if (!isQueryGone(q)) {
        // Fetched after the directory: the server already answered for it since.
        if (q.state.dataUpdatedAt > list.dataUpdatedAt) return;
        await qc.fetchQuery({ queryKey: memberKey(id), queryFn: ({ signal }) => fetchMember(id, signal), meta: { persist: true }, staleTime: 0 }).catch(() => undefined);
      }
      // The profile may have opened meanwhile: then its screen decides.
      const now = qc.getQueryCache().find({ queryKey: memberKey(id), exact: true });
      if (now && now.getObserversCount() === 0 && isQueryGone(now)) qc.removeQueries({ queryKey: memberKey(id), exact: true });
    }),
  );
}

export function msSinceCorePrefetch(): number {
  return lastRunAt ? Date.now() - lastRunAt : Infinity;
}

const REFRESH_AFTER_HIDDEN_MS = 30 * 60_000;

/**
 * Mounted once while signed in (OfflineRuntime): prefetches the core queries on start, when the
 * network comes back, and when the app becomes visible after a long pause.
 */
export function usePrefetchCore(): void {
  const qc = useQueryClient();
  useEffect(() => {
    const run = () => void prefetchCore(qc);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && msSinceCorePrefetch() > REFRESH_AFTER_HIDDEN_MS) run();
    };
    run();
    window.addEventListener('online', run);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', run);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [qc]);
}
